import {injectable, inject} from 'inversify';

import {Inject} from '../common/Injectables';
import {ServiceStore} from '../store/ServiceStore';
import {Action} from '../store/Store';
import {RootState} from '../store/State';
import {UpdateDirectory, UpdateAppStatus, LaunchApp} from '../store/partials/Directory';
import {PostLogin} from '../store/partials/Authentication';
import {API} from '../model/API';
import {UserData} from '../model/api/User';
import {App, AppStatusStates} from '../types/commons';
import {ManifestType} from '../types/manifest';

import {AsyncInit} from './AsyncInit';

@injectable()
export class Directory extends AsyncInit {
    public constructor(
        @inject(Inject.API) private readonly api: API,
        @inject(Inject.STORE) private readonly store: ServiceStore
    ) {
        super();
        this.store.onAction.add(this.onAction, this);
    }

    protected async init() {
        await this.store.initialized;
    }

    private async onAction(action: Action<RootState>): Promise<void> {
        if (action instanceof PostLogin) {
            const apps = await this.fetchApps();
            await this.store.dispatch(new UpdateDirectory(apps));
        } else if (action instanceof LaunchApp) {
            const status = await this.launchApp(action.app);
            await this.store.dispatch(new UpdateAppStatus(action.app, status));
        }
    }

    private async fetchApps(): Promise<App[]> {
        const apps: App[] = await this.api.get(UserData.APPS, {urlParams: {scope: 'all'}});
        return apps;
    }

    public async launchApp(app: App): Promise<AppStatusStates> {
        const {manifest, manifestType, name} = app;

        if (!manifest) {
            throw new Error(`No manifest_url or appUrl specified for this application (${name})`);
        }

        if (manifestType === ManifestType.PATH) {
            // Native app - use launchExternalProcess
            try {
                await fin.System.launchExternalProcess({path: manifest});
                return AppStatusStates.Running;
            } catch (error) {
                return AppStatusStates.Error;
            }
        } else {
            try {
                await fin.Application.startFromManifest(manifest);
                return AppStatusStates.Running;
            } catch (error) {
                console.error(error);
                return AppStatusStates.Error;
            }
        }
    }
}

